import React from 'react';
import { ArrowRight, Globe2 } from 'lucide-react';

export const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1769837496184-32831581c55a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxzaGlwcGluZyUyMGNvbnRhaW5lciUyMHBvcnQlMjBuaWdodHxlbnwwfHx8fDE3NzI2NDA4OTB8MA&ixlib=rb-4.1.0&q=85"
          alt="Global Trade"
          className="w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Decorative Globe Element */}
      <div className="absolute top-20 right-10 opacity-10 animate-glow hidden lg:block">
        <Globe2 size={300} className="text-gold" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 text-center">
        {/* Logo - Much Bigger */}
        <div className="mb-8 animate-fade-in flex justify-center">
          <img 
            src="/agatsya-logo.png" 
            alt="Agatsya International" 
            className="h-56 md:h-72 lg:h-80 w-auto"
          />
        </div>

        {/* Value Proposition */}
        <div className="mb-12 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-heading font-semibold text-white mb-6 leading-tight">
            Bridging Global Trade, Industrial Innovation,
            <br />
            <span className="text-gold">and Financial Excellence</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Your trusted partner in premium agricultural exports, industrial chemicals, and comprehensive financial solutions. 
            Bridging global trade with quality products and strategic excellence.
          </p>
        </div>

        {/* CTA Buttons */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-6 animate-fade-in-up"
          style={{ animationDelay: '0.4s' }}
        >
          <button
            onClick={() => scrollToSection('export')}
            className="btn-gold group flex items-center gap-3"
            data-testid="hero-explore-btn"
          >
            Explore Products
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={() => scrollToSection('contact')}
            className="btn-silver group flex items-center gap-3"
            data-testid="hero-inquiry-btn"
          >
            Send Inquiry
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <a
            href="/agatsya-brochure.pdf"
            download
            className="btn-silver group flex items-center gap-3"
            data-testid="hero-brochure-btn"
          >
            Download Brochure
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        {/* Stats */}
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-24 animate-fade-in-up"
          style={{ animationDelay: '0.6s' }}
        >
          <div className="glass-panel p-6 rounded-lg">
            <div className="text-3xl md:text-4xl font-bold text-gold-gradient mb-2">Direct</div>
            <div className="text-sm md:text-base text-gray-400 uppercase tracking-wider">Sourcing</div>
          </div>
          <div className="glass-panel p-6 rounded-lg">
            <div className="text-3xl md:text-4xl font-bold text-gold-gradient mb-2">Strategic</div>
            <div className="text-sm md:text-base text-gray-400 uppercase tracking-wider">Solution</div>
          </div>
          <div className="glass-panel p-6 rounded-lg">
            <div className="text-3xl md:text-4xl font-bold text-gold-gradient mb-2">Global</div>
            <div className="text-sm md:text-base text-gray-400 uppercase tracking-wider">Reach</div>
          </div>
          <div className="glass-panel p-6 rounded-lg">
            <div className="text-3xl md:text-4xl font-bold text-gold-gradient mb-2">100%</div>
            <div className="text-sm md:text-base text-gray-400 uppercase tracking-wider">Quality Assured</div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-gold rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-gold rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};
